document.addEventListener('DOMContentLoaded', function () {
  const easyEvents = {
    indexChanged: 'custom:easysearch_index_changed',
    notify: 'custom:easysearch_notify',
    ready: 'easysearch_ready',
    loaded: 'easysearch_loaded',
  };

  const storageKeys = {
    index: 'custom:easysearch-index',
    treeValue: 'custom:easysearch-value',
    dbVersion: 'custom:easysearch-db-version',
  };

  // Bump whenever the EasySearch vehicle DB changes shape. A stored vehicle
  // stamped with an older version is dropped so the shopper re-picks and gets
  // a current URL. 2026-07-28: every DB row now carries a /fit-<model>-<year>
  // segment, so a stored URL without one predates the migration and would fall
  // back to collection-membership matching (i.e. claim "fits" without checking).
  const DB_VERSION = '2026-07-28';

  // Products carrying this tag are not vehicle specific (winches, light bars,
  // ropes). They get a neutral "universal fit" label instead of a verdict.
  const UNIVERSAL_TAG = 'fit-universal';

  function saveToLocalStorage(storageKey, storageValue) {
    localStorage.setItem(storageKey, storageValue);
  }

  function getFromLocalStorage(storageKey) {
    return localStorage.getItem(storageKey) ?? false;
  }

  function purgeStaleVehicle() {
    try {
      const stored = localStorage.getItem(storageKeys.treeValue);
      const stamped = localStorage.getItem(storageKeys.dbVersion);
      const hasFitTag = !!stored && /\/collections\/[^/]+\/fit-/.test(stored);
      // stamped === null means the vehicle predates versioning: keep it if its
      // URL is already tag-shaped, drop it otherwise.
      const versionMismatch = stamped !== null && stamped !== DB_VERSION;

      if (stored && (!hasFitTag || versionMismatch)) {
        localStorage.removeItem(storageKeys.treeValue);
        localStorage.removeItem(storageKeys.index);
      }
      localStorage.setItem(storageKeys.dbVersion, DB_VERSION);
    } catch (e) {
      /* private mode / storage disabled: nothing to purge */
    }
  }

  purgeStaleVehicle();

  function getEasysearchTreeValues(curIndex = '') {
    if (!window.easysearch?.tree) return [];
    const key = curIndex && !curIndex.endsWith(',') ? `${curIndex},` : curIndex;
    return easysearch.tree[key] ?? [];
  }

  function interceptIndexChanges() {
    const applyInterceptor = () => {
      let currentIndex = easysearch.curIndex;
      Object.defineProperty(easysearch, 'curIndex', {
        get: () => currentIndex,
        set: (updatedIndex) => {
          const prevIndex = currentIndex;
          currentIndex = updatedIndex;
          if (updatedIndex !== prevIndex) {
            document.dispatchEvent(
              new CustomEvent(easyEvents.indexChanged, {
                bubbles: true,
                detail: { curIndex: updatedIndex, prevIndex },
              }),
            );
          }
        },
        configurable: true,
      });
    };

    window.easysearch
      ? applyInterceptor()
      : document.addEventListener(easyEvents.ready, applyInterceptor, { once: true });
  }

  function notifyWhenEasysearchReady() {
    const dispatchNotifyEvent = () => document.dispatchEvent(new CustomEvent(easyEvents.notify));

    if (window.easysearch) {
      dispatchNotifyEvent();
    } else {
      document.addEventListener(easyEvents.ready, dispatchNotifyEvent, { once: true });
    }
  }

  function init() {
    hideSections();
    interceptIndexChanges();
    notifyWhenEasysearchReady();
  }

  function delay(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  function parseCollectionsJson(scriptElement) {
    try {
      return JSON.parse(scriptElement.textContent);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  function parseTagsJson(scriptElement) {
    if (!scriptElement) return [];
    try {
      return JSON.parse(scriptElement.textContent).map((tag) => String(tag).toLowerCase());
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  function splitVehicleUrl(treeValue) {
    const pathname = new URL(treeValue).pathname;
    const segments = pathname.split('/').filter(Boolean);
    const collectionsIndex = segments.indexOf('collections');

    if (collectionsIndex === -1 || !segments[collectionsIndex + 1]) {
      return { collectionPathname: pathname, fitTag: '' };
    }

    const collectionPathname = `/${segments.slice(0, collectionsIndex + 2).join('/')}`;
    const fitTag = decodeURIComponent(segments[collectionsIndex + 2] ?? '').toLowerCase();
    return { collectionPathname, fitTag };
  }

  // Vehicle URLs may carry a fitment tag segment (/collections/handle/fit-model-year).
  // Collection membership alone is not fitment: when the tag is present, the product
  // must carry it; without a tag (non-migrated vehicles) membership decides, as before.
  function isUniversal(productTags) {
    return productTags.includes(UNIVERSAL_TAG);
  }

  function resolveFitState(collections, productTags, treeValue) {
    const { collectionPathname, fitTag } = splitVehicleUrl(treeValue);

    // Fitment data comes first. A universal part answers 'universal', and a
    // product with no fit data at all answers 'unknown' (renders nothing):
    // both are outside the year collections, so the membership test below
    // would otherwise reject them against every truck.
    if (isUniversal(productTags)) return 'universal';
    if (!productTags.some((productTag) => productTag.startsWith('fit-'))) return 'unknown';

    const matchedCollection = collections.find(({ url }) => new URL(url).pathname === collectionPathname);

    if (!matchedCollection) return 'reject';
    // No fit tag in the vehicle URL means the year cannot be checked. Stay
    // silent ('default' renders no badge) rather than claiming a match.
    if (!fitTag) return 'default';
    return productTags.includes(fitTag) ? 'success' : 'reject';
  }

  function hideSections() {
    const treeValue = getFromLocalStorage(storageKeys.treeValue);
    const selected = !!(treeValue && treeValue.includes('https'));
    const templateNames = ['collection'];
    const selectors = templateNames
      .map((name) => `.shopify-section:has(.easy-search[data-template="${name}"])`)
      .join(',');
    const easySections = document.querySelectorAll(selectors);

    easySections.forEach((section) => section.classList.toggle('hidden', selected));
  }

  document.addEventListener(easyEvents.indexChanged, ({ detail }) => {
    const [treeValue] = getEasysearchTreeValues(detail.curIndex);

    if (treeValue) {
      saveToLocalStorage(storageKeys.treeValue, treeValue);
    } else {
      localStorage.removeItem(storageKeys.treeValue);
    }

    if (detail.curIndex) {
      saveToLocalStorage(storageKeys.index, detail.curIndex);
    } else {
      localStorage.removeItem(storageKeys.index);
    }
  });

  class EasySearchHeaderButton extends HTMLElement {
    constructor() {
      super();

      this.changeButtonLabelElement = null;
      this.easySearchModalElement = null;
      this.onAppReady = this.onAppReady.bind(this);
      this.onChangeIndex = this.onChangeIndex.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.changeButtonLabelElement = this.querySelector('.easysearch-button-change__inner');
      this.easySearchModalElement = document.querySelector('easysearch-modal');

      document.addEventListener(easyEvents.notify, this.onAppReady, { once: true });
      document.addEventListener(easyEvents.indexChanged, this.onChangeIndex);
      if (this.easySearchModalElement) this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      document.removeEventListener(easyEvents.indexChanged, this.onChangeIndex);
      if (this.easySearchModalElement) this.removeEventListener('click', this.onClick);
    }

    updateAttribute(attributeName, attributeValue) {
      this.setAttribute(attributeName, attributeValue);
    }

    onAppReady() {
      this.updateAttribute('data-loaded', 'true');
      this.updateLabel();
    }

    updateLabel() {
      if (!this.changeButtonLabelElement) return;
      const [index, treeValue] = [getFromLocalStorage(storageKeys.index), getFromLocalStorage(storageKeys.treeValue)];

      if (index && treeValue && treeValue.includes('https')) {
        this.updateAttribute('data-selected', 'true');
        const vehicleNameText = index.split(',').join(' ').trim();
        this.changeButtonLabelElement.textContent = vehicleNameText;
      }
    }

    onChangeIndex() {
      this.updateLabel();
    }

    onClick() {
      this.easySearchModalElement.setAttribute('open', 'true');
    }
  }

  class EasySearchModal extends HTMLElement {
    static observedAttributes = ['open'];

    constructor() {
      super();

      this.closeTriggerElements = null;
      this.boundOnClose = this.onClose.bind(this);
    }

    connectedCallback() {
      this.closeTriggerElements = this.querySelectorAll('.easysearch-modal__overlay, .easysearch-modal__close');
      this.closeTriggerElements.forEach((closeTriggerElement) =>
        closeTriggerElement.addEventListener('click', this.boundOnClose),
      );
    }

    disconnectedCallback() {
      this.closeTriggerElements.forEach((closeTriggerElement) =>
        closeTriggerElement.removeEventListener('click', this.boundOnClose),
      );
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'open') {
        document.body.classList.toggle('overflow-hidden', newValue !== null);
      }
    }

    onClose() {
      this.removeAttribute('open');
    }
  }

  class EasySearchProductBlock extends HTMLElement {
    constructor() {
      super();

      this.collections = [];
      this.productTags = [];
      this.vehicleNameElements = null;
      this.rejectCtaElement = null;

      this.onAppReady = this.onAppReady.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onChangeIndex = this.onChangeIndex.bind(this);
    }

    connectedCallback() {
      this.collections = parseCollectionsJson(this.querySelector('script[data-collections]'));
      this.productTags = parseTagsJson(this.querySelector('script[data-product-tags]'));
      this.vehicleNameElements = this.querySelectorAll('span[data-vehicle-name]');
      this.rejectCtaElement = this.querySelector('[data-reject-cta]');

      document.addEventListener(easyEvents.notify, this.onAppReady, { once: true });
      document.addEventListener(easyEvents.indexChanged, this.onChangeIndex);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener(easyEvents.indexChanged, this.onChangeIndex);
    }

    onAppReady() {
      this.updateAttribute('data-loaded', 'true');
      this.determineStatus();
    }

    updateAttribute(attributeName, attributeValue) {
      this.setAttribute(attributeName, attributeValue);
    }

    determineStatus() {
      const [index, treeValue] = [getFromLocalStorage(storageKeys.index), getFromLocalStorage(storageKeys.treeValue)];

      // A universal part gives the same answer with or without a vehicle, so it
      // never shows the picker: the label stands on its own.
      if (isUniversal(this.productTags)) {
        this.updateAttribute('data-state', 'universal');
        return;
      }

      if (!treeValue || !treeValue.includes('https')) {
        this.updateAttribute('data-state', 'default');
        return;
      }

      this.updateAttribute('data-state', resolveFitState(this.collections, this.productTags, treeValue));
      this.updateVehicleLabels(index);
      if (this.rejectCtaElement instanceof HTMLAnchorElement) {
        this.rejectCtaElement.href = treeValue;
      }
    }

    updateVehicleLabels(vehicleIndex) {
      if (!this.vehicleNameElements || this.vehicleNameElements.length === 0) return;
      const vehicleNameText = vehicleIndex.split(',').join(' ').trim();
      this.vehicleNameElements.forEach(
        (vehicleNameElement) => (vehicleNameElement.textContent = ` ${vehicleNameText}`),
      );
    }

    onClick(event) {
      const { target } = event;
      const changeButtonElement = target.closest('.easysearch-product__change-button');
      const searchButtonElement = target.closest('.easysearch-btn-search');
      if (changeButtonElement) {
        this.updateAttribute('data-state', 'default');
      } else if (searchButtonElement) {
        this.determineStatus();
        event.preventDefault();
      }
    }

    onChangeIndex() {
      const isDefaultState = this.dataset.state === 'default';
      if (isDefaultState) return;
      const treeValue = getFromLocalStorage(storageKeys.treeValue);
      if (treeValue && treeValue.includes('https')) this.determineStatus();
    }
  }

  class EasySearchTextLine extends HTMLElement {
    static observer = null;

    static getObserver() {
      if (EasySearchTextLine.observer) return EasySearchTextLine.observer;
      EasySearchTextLine.observer = new IntersectionObserver((intersectionEntries) => {
        intersectionEntries.forEach((intersectionEntry) => {
          if (!intersectionEntry.isIntersecting) return;
          intersectionEntry.target.onIntersect();
          EasySearchTextLine.observer.unobserve(intersectionEntry.target);
        });
      });
      return EasySearchTextLine.observer;
    }

    constructor() {
      super();

      this.collections = [];
      this.productTags = [];
      this.vehicleNameElements = null;

      this.onAppReady = this.onAppReady.bind(this);
      this.onChangeIndex = this.onChangeIndex.bind(this);
    }

    connectedCallback() {
      this.collections = parseCollectionsJson(this.querySelector('script[data-collections]'));
      this.productTags = parseTagsJson(this.querySelector('script[data-product-tags]'));
      this.vehicleNameElements = this.querySelectorAll('[data-vehicle-name]');

      document.addEventListener(easyEvents.indexChanged, this.onChangeIndex);
      EasySearchTextLine.getObserver().observe(this);
    }

    disconnectedCallback() {
      document.removeEventListener(easyEvents.notify, this.onAppReady);
      document.removeEventListener(easyEvents.indexChanged, this.onChangeIndex);
      EasySearchTextLine.getObserver().unobserve(this);
    }

    updateAttribute(attributeName, attributeValue) {
      this.setAttribute(attributeName, attributeValue);
    }

    onIntersect() {
      if (window.easysearch) {
        this.onAppReady();
      } else {
        document.addEventListener(easyEvents.notify, this.onAppReady, { once: true });
      }
    }

    onAppReady() {
      this.updateAttribute('data-loaded', 'true');
      this.determineStatus();
    }

    determineStatus() {
      const [index, treeValue] = [getFromLocalStorage(storageKeys.index), getFromLocalStorage(storageKeys.treeValue)];

      // A universal part gives the same answer with or without a vehicle, so it
      // never shows the picker: the label stands on its own.
      if (isUniversal(this.productTags)) {
        this.updateAttribute('data-state', 'universal');
        return;
      }

      if (!treeValue || !treeValue.includes('https')) {
        this.updateAttribute('data-state', 'default');
        return;
      }

      this.updateAttribute('data-state', resolveFitState(this.collections, this.productTags, treeValue));
      this.updateVehicleLabels(index);
    }

    updateVehicleLabels(vehicleIndex) {
      if (!this.vehicleNameElements || this.vehicleNameElements.length === 0) return;
      const vehicleNameText = vehicleIndex.split(',').join(' ').trim();
      this.vehicleNameElements.forEach(
        (vehicleNameElement) => (vehicleNameElement.textContent = ` ${vehicleNameText}`),
      );
    }

    onChangeIndex() {
      this.determineStatus();
    }
  }

  class EasySearchReset extends HTMLElement {
    constructor() {
      super();

      this.boundOnReset = this.onReset.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.boundOnReset);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundOnReset);
    }

    onReset() {
      if (!window.easysearch) return;
      easysearch.curIndex = '';
      easysearch.setCookie('easysearch-tag-filter', '');
      easysearch.setCookie('easysearch-preselect', '');
      easysearch.setCookie('easysearch-preselect-fitment', '');
      easysearch.currentTag = '';
      easysearch.searchFormClearSelects(this.closest(".easysearch-embed-widget-main"));
      document
        .querySelectorAll('easysearch-header-button')
        .forEach((button) => button.setAttribute('data-selected', 'false'));
    }
  }

  if (!customElements.get('easysearch-header-button')) {
    customElements.define('easysearch-header-button', EasySearchHeaderButton);
  }

  if (!customElements.get('easysearch-modal')) {
    customElements.define('easysearch-modal', EasySearchModal);
  }

  if (!customElements.get('easysearch-product-block')) {
    customElements.define('easysearch-product-block', EasySearchProductBlock);
  }

  if (!customElements.get('easysearch-text-line')) {
    customElements.define('easysearch-text-line', EasySearchTextLine);
  }

  if (!customElements.get('easysearch-reset')) {
    customElements.define('easysearch-reset', EasySearchReset);
  }

  delay(200).then(init);
});
